import React, { useState, useRef, useEffect } from 'react';
import { ListFilter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import {
  smallTextSize,
  semiBoldText,
  whiteBG,
  borderPrimary,
  secondaryText,
  darkGreenTextColor,
} from '../../styling/stylingHelper';
import { PriceAlertChangeType } from '../../stores/appFiltersStore';
import { FilterModal } from './FilterModal';

interface FilterButtonProps {
  // Order filters - Restaurant filters
  restaurantFilters?: string[];
  setRestaurantFilters?: (filters: string[]) => void;
  availableRestaurants?: { id: string; name: string }[];

  // Order filters - Platform filters
  platformFilters?: string[];
  setPlatformFilters?: (filters: string[]) => void;
  availablePlatforms?: string[];
  sectionFilters?: string[];
  setSectionFilters?: (filters: string[]) => void;
  availableSections?: { id: string; name: string }[];
  categoryFilters?: string[];
  setCategoryFilters?: (filters: string[]) => void;
  availableCategories?: string[];
  subCategoryFilters?: string[];
  setSubCategoryFilters?: (filters: string[]) => void;
  availableSubCategories?: string[];
  // Revenue filters
  revenueRestaurantFilters?: string[];
  setRevenueRestaurantFilters?: (filters: string[]) => void;
  availableRevenueRestaurants?: { id: string; name: string }[];
  revenueCategoryFilters?: string[];
  setRevenueCategoryFilters?: (filters: string[]) => void;
  availableRevenueCategories?: string[];
  revenueSourceFilters?: string[];
  setRevenueSourceFilters?: (filters: string[]) => void;
  availableRevenueSources?: string[];
  menuCategoryFilters?: string[];
  setMenuCategoryFilters?: (filters: string[]) => void;
  availableMenuCategories?: { id: string; name: string }[];
  // Price alert filters
  priceAlertRestaurantFilters?: string[];
  setPriceAlertRestaurantFilters?: (filters: string[]) => void;
  availablePriceAlertRestaurants?: { id: string; name: string }[];
  priceAlertPlatformFilters?: string[];
  setPriceAlertPlatformFilters?: (filters: string[]) => void;
  availablePriceAlertPlatforms?: string[];
  priceAlertSectionFilters?: string[];
  setPriceAlertSectionFilters?: (filters: string[]) => void;
  availablePriceAlertSections?: { id: string; name: string }[];
  priceAlertCategoryFilters?: string[];
  setPriceAlertCategoryFilters?: (filters: string[]) => void;
  availablePriceAlertCategories?: string[];
  // Price alert change type filter
  priceAlertChangeType?: PriceAlertChangeType;
  setPriceAlertChangeType?: (type: PriceAlertChangeType) => void;
  // VAT toggle (for mobile view)
  onVatChange?: (value: string) => void;
  currentVatValue?: string;
  // Common
  onResetFilters: () => void;
  activeFilterCount: number;
  align?: 'left' | 'right' | 'center';
  showLabel?: boolean;
  /**
   * 'chip' = compact green-outline pill (the designer's mobile "Customize" slot),
   * sized to sit inline in the mobile filter-chips row. Default keeps the
   * full-width bordered button used on desktop toolbars.
   */
  variant?: 'default' | 'chip';
}

export const FilterButton: React.FC<FilterButtonProps> = ({
  restaurantFilters,
  setRestaurantFilters,
  availableRestaurants,
  platformFilters,
  setPlatformFilters,
  availablePlatforms,
  sectionFilters,
  setSectionFilters,
  availableSections,
  categoryFilters,
  setCategoryFilters,
  availableCategories,
  subCategoryFilters,
  setSubCategoryFilters,
  availableSubCategories,
  revenueRestaurantFilters,
  setRevenueRestaurantFilters,
  availableRevenueRestaurants,
  revenueCategoryFilters,
  setRevenueCategoryFilters,
  availableRevenueCategories,
  revenueSourceFilters,
  setRevenueSourceFilters,
  availableRevenueSources,
  menuCategoryFilters,
  setMenuCategoryFilters,
  availableMenuCategories,
  priceAlertRestaurantFilters,
  setPriceAlertRestaurantFilters,
  availablePriceAlertRestaurants,
  priceAlertPlatformFilters,
  setPriceAlertPlatformFilters,
  availablePriceAlertPlatforms,
  priceAlertSectionFilters,
  setPriceAlertSectionFilters,
  availablePriceAlertSections,
  priceAlertCategoryFilters,
  setPriceAlertCategoryFilters,
  availablePriceAlertCategories,
  priceAlertChangeType,
  setPriceAlertChangeType,
  onVatChange,
  currentVatValue,
  onResetFilters,
  activeFilterCount,
  align = 'right',
  showLabel = true,
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Detect if we're in mobile view (below 1100px)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1100);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowFilterModal(false);
      }
    };

    if (showFilterModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterModal]);

  return (
    <div ref={containerRef} className="relative flex justify-end">
      <button
        onClick={() => setShowFilterModal(!showFilterModal)}
        className={cn(
          'gap-2 px-4 py-2 rounded-xl border transition-colors',
          smallTextSize,
          semiBoldText,
          whiteBG,
          variant === 'chip'
            ? cn(
                'flex items-center justify-center whitespace-nowrap',
                'border-darkGreenColor dark:border-darkGreenColor-dark',
                darkGreenTextColor
              )
            : cn(
                'flex w-full justify-center items-center min-[550px]:max-w-[250px]',
                borderPrimary,
                secondaryText
              ),
          'hover:bg-gray-50 dark:hover:bg-white/5'
        )}
      >
        <ListFilter className="w-4 h-4" />
        {showLabel && <span>{t('common.filters')}</span>}
        {activeFilterCount > 0 && (
          <span className="bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {activeFilterCount}
          </span>
        )}
      </button>
      {showFilterModal && (
        <FilterModal
          restaurantFilters={restaurantFilters}
          setRestaurantFilters={setRestaurantFilters}
          availableRestaurants={availableRestaurants}
          platformFilters={platformFilters}
          setPlatformFilters={setPlatformFilters}
          availablePlatforms={availablePlatforms}
          sectionFilters={sectionFilters}
          setSectionFilters={setSectionFilters}
          availableSections={availableSections}
          categoryFilters={categoryFilters}
          setCategoryFilters={setCategoryFilters}
          availableCategories={availableCategories}
          subCategoryFilters={subCategoryFilters}
          setSubCategoryFilters={setSubCategoryFilters}
          availableSubCategories={availableSubCategories}
          revenueRestaurantFilters={revenueRestaurantFilters}
          setRevenueRestaurantFilters={setRevenueRestaurantFilters}
          availableRevenueRestaurants={availableRevenueRestaurants}
          revenueCategoryFilters={revenueCategoryFilters}
          setRevenueCategoryFilters={setRevenueCategoryFilters}
          availableRevenueCategories={availableRevenueCategories}
          revenueSourceFilters={revenueSourceFilters}
          setRevenueSourceFilters={setRevenueSourceFilters}
          availableRevenueSources={availableRevenueSources}
          menuCategoryFilters={menuCategoryFilters}
          setMenuCategoryFilters={setMenuCategoryFilters}
          availableMenuCategories={availableMenuCategories}
          priceAlertRestaurantFilters={priceAlertRestaurantFilters}
          setPriceAlertRestaurantFilters={setPriceAlertRestaurantFilters}
          availablePriceAlertRestaurants={availablePriceAlertRestaurants}
          priceAlertPlatformFilters={priceAlertPlatformFilters}
          setPriceAlertPlatformFilters={setPriceAlertPlatformFilters}
          availablePriceAlertPlatforms={availablePriceAlertPlatforms}
          priceAlertSectionFilters={priceAlertSectionFilters}
          setPriceAlertSectionFilters={setPriceAlertSectionFilters}
          availablePriceAlertSections={availablePriceAlertSections}
          priceAlertCategoryFilters={priceAlertCategoryFilters}
          setPriceAlertCategoryFilters={setPriceAlertCategoryFilters}
          availablePriceAlertCategories={availablePriceAlertCategories}
          priceAlertChangeType={priceAlertChangeType}
          setPriceAlertChangeType={setPriceAlertChangeType}
          onVatChange={onVatChange}
          currentVatValue={currentVatValue}
          showVatToggle={isMobile}
          onClose={() => setShowFilterModal(false)}
          onReset={() => {
            onResetFilters();
            setShowFilterModal(false);
          }}
          align={align}
        />
      )}
    </div>
  );
};
